import React, { useState, useRef, useEffect } from 'react';
import { IconUpload, IconPlus, IconTrash, IconStar, IconStarFilled, IconCheck, IconAlert } from './Icons';

interface PhotoGalleryManagerProps {
  photos: string[];
  avatarUrl: string;
  onChangePhotos: (photos: string[], newAvatarUrl: string) => void;
}

export const PhotoGalleryManager: React.FC<PhotoGalleryManagerProps> = ({
  photos,
  avatarUrl,
  onChangePhotos
}) => {
  const [urlInput, setUrlInput] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  // Сжатие изображения через Canvas для оптимизации памяти и трафика
  const processImageFile = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const maxDimension = 1200;
          let { width, height } = img;

          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = Math.round((height * maxDimension) / width);
              width = maxDimension;
            } else {
              width = Math.round((width * maxDimension) / height);
              height = maxDimension;
            }
          }

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            resolve(e.target?.result as string);
            return;
          }

          ctx.drawImage(img, 0, 0, width, height);
          const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.85);
          resolve(compressedDataUrl);
        };
        img.onerror = () => resolve(e.target?.result as string);
        img.src = e.target?.result as string;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const addImages = async (newImages: string[]) => {
    if (newImages.length === 0) return;
    const updated = [...photos, ...newImages];
    // Если аватарки еще нет, ставим первую загруженную
    const newAvatar = avatarUrl && photos.includes(avatarUrl) ? avatarUrl : (updated[0] || '');
    onChangePhotos(updated, newAvatar);
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setIsProcessing(true);
    try {
      const promises: Promise<string>[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.type.startsWith('image/')) {
          promises.push(processImageFile(file));
        }
      }
      const base64List = await Promise.all(promises);
      await addImages(base64List);
    } catch (err) {
      console.error('Ошибка при обработке файлов:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  // Перетаскивание (Drag & Drop)
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    await handleFiles(e.dataTransfer.files);
  };

  // Вставка из буфера обмена (Paste / Ctrl+V / Cmd+V)
  useEffect(() => {
    const handlePaste = async (e: Event) => {
      const clipEvent = e as ClipboardEvent;
      const items = clipEvent.clipboardData?.items;
      if (!items) return;

      const imageFiles: File[] = [];
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith('image/')) {
          const file = items[i].getAsFile();
          if (file) imageFiles.push(file);
        }
      }

      if (imageFiles.length > 0) {
        setIsProcessing(true);
        try {
          const promises = imageFiles.map(f => processImageFile(f));
          const base64List = await Promise.all(promises);
          await addImages(base64List);
        } catch (err) {
          console.error('Ошибка вставки из буфера:', err);
        } finally {
          setIsProcessing(false);
        }
      }
    };

    window.addEventListener('paste', handlePaste as EventListener);
    return () => window.removeEventListener('paste', handlePaste as EventListener);
  }, [photos, avatarUrl]);

  const handleAddUrl = (e: React.FormEvent) => {
    e.preventDefault();
    if (!urlInput.trim()) return;
    addImages([urlInput.trim()]);
    setUrlInput('');
  };

  const handleSetAvatar = (photoUrl: string) => {
    onChangePhotos(photos, photoUrl);
  };

  const handleDeletePhoto = (indexToDelete: number) => {
    const photoToDelete = photos[indexToDelete];
    const updated = photos.filter((_, i) => i !== indexToDelete);
    let newAvatar = avatarUrl;
    if (avatarUrl === photoToDelete) {
      newAvatar = updated[0] || '';
    }
    onChangePhotos(updated, newAvatar);
  };

  return (
    <div className="photo-gallery-manager">
      {/* Зона загрузки с поддержкой Drag&Drop, Paste и выбора файлов */}
      <div 
        ref={dropZoneRef}
        className={`photo-drop-zone ${isDragging ? 'dragging' : ''} ${isProcessing ? 'processing' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input 
          ref={fileInputRef}
          type="file" 
          multiple 
          accept="image/*" 
          style={{ display: 'none' }}
          onChange={(e) => handleFiles(e.target.files)}
        />

        <div className="drop-zone-content">
          <div className="drop-zone-icon-box">
            <IconUpload size={22} />
          </div>
          <div className="drop-zone-text">
            <h4>{isProcessing ? 'ОБРАБОТКА ИЗОБРАЖЕНИЙ...' : 'ПЕРЕТАЩИТЕ ФАЙЛЫ ИЛИ НАЖМИТЕ ДЛЯ ВЫБОРА'}</h4>
            <p className="drop-zone-sub">
              Мультизагрузка, Drag & Drop и прямая вставка из буфера (Ctrl+V / Cmd+V)
            </p>
          </div>
        </div>
      </div>

      {/* Быстрое добавление по URL */}
      <div className="url-add-row">
        <input 
          type="url" 
          placeholder="Вставьте прямую ссылку на фото (https://...)"
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleAddUrl(e); }}
        />
        <button type="button" className="btn btn-secondary" onClick={handleAddUrl}>
          <IconPlus size={13} />
          <span>ДОБАВИТЬ URL</span>
        </button>
      </div>

      {/* Галерея загруженных фото */}
      <div className="gallery-header-bar">
        <span className="gallery-count">
          АКТИВНЫХ ФОТО: <strong>{photos.length}</strong>
        </span>
        <span className="gallery-hint">
          Выберите изображение для назначения основным аватаром профиля
        </span>
      </div>

      {photos.length === 0 ? (
        <div className="no-photos-alert">
          <IconAlert size={14} />
          <span>Фотографии еще не загружены. Перетащите файлы в область выше или нажмите Ctrl+V.</span>
        </div>
      ) : (
        <div className="photos-grid">
          {photos.map((photo, index) => {
            const isAvatar = photo === avatarUrl;

            return (
              <div key={index} className={`photo-card ${isAvatar ? 'is-avatar' : ''}`}>
                <div className="photo-thumb-wrap">
                  <img src={photo} alt={`Photo ${index + 1}`} />
                  {isAvatar && (
                    <div className="avatar-tag-badge">
                      <IconStarFilled size={10} />
                      <span>ОСНОВНОЙ АВАТАР</span>
                    </div>
                  )}
                </div>

                <div className="photo-card-actions">
                  {!isAvatar ? (
                    <button 
                      type="button" 
                      className="btn-set-avatar"
                      title="Назначить главным аватаром бота"
                      onClick={() => handleSetAvatar(photo)}
                    >
                      <IconStar size={12} />
                      <span>НАЗНАЧИТЬ</span>
                    </button>
                  ) : (
                    <span className="avatar-active-label">
                      <IconCheck size={12} />
                      <span>АКТИВЕН</span>
                    </span>
                  )}

                  <button 
                    type="button" 
                    className="btn-delete-photo"
                    title="Удалить фото"
                    onClick={() => handleDeletePhoto(index)}
                  >
                    <IconTrash size={12} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
